import React, { useState, useEffect, useContext, createContext } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { LocationProvider } from '../context/LocationContext'
import { UserProvider, UserContext } from '../context/UserContext'
import '../styles/globals.css'
import { useRouter } from 'next/router'
import { HomeHeader } from "../components/home/HomeHeader"
import { MenuBar } from "../components/home/MenuBar"
import { HardLoadingCover } from "../components/commons/HardLoadingCover";
import { AndroidAppInstall } from "../components/commons/AndroidAppInstall"
import { IosAppInstall } from "../components/commons/IosAppInstall"
import { PreserveScroll } from "../components/commons/PreserveScroll"
import { Footer } from "../components/index/Footer"
import { RequestProvider } from "../context/RequestContext"
import { IndexNav } from "@/index/IndexNav"
import Script from "next/script"

// Create context for gtag loading state
export const GtagContext = createContext({ isGtagLoaded: false, setIsGtagLoaded: () => {} })

function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="text-center h-screen relative bg-white">
      <div className="absolute top-0 bottom-0 m-auto flex justify-center items-center w-full">
        <div>
          <h2 className="text-lg font-semibold">
            Session expired for security reasons
          </h2>
          <button
            onClick={() => {
              resetErrorBoundary()
              window.location.reload()
            }}
            className="mt-6 px-20 py-6 bg-linear-to-tr from-orange-600 to-amber-400 text-white rounded-full"
          >
            Reload App
          </button>
        </div>
      </div>
    </div>
  )
}

// Inner component that has access to UserContext
const AppContent = ({ Component, pageProps, setIsUserLoggedIn, isUserLoggedIn, setIsShowFooter }) => {
  const router = useRouter()
  const { isCheckingLoginStatus } = useContext(UserContext)
  const [isHome, setIsHome] = useState(false)
  const [isShowAndroidInstall, setIsShowAndroidInstall] = useState(false)
  const [isShowIosInstall, setIsShowIosInstall] = useState(false)
  const [isMobileApp, setIsMobileApp] = useState(false)

  useEffect(() => {
    // Detect if user is using the StringsSG mobile app (iOS or Android)
    if (typeof navigator !== 'undefined') {
      const isStringsSGApp = /StringsSG\/[0-9\.]+$/.test(navigator.userAgent)
      setIsMobileApp(isStringsSGApp)
    }
  }, [])

  useEffect(() => {
    if (isUserLoggedIn) {
      setIsHome(true)
      const ua = navigator.userAgent;
      if (/Android/i.test(ua)) {
        // Matches if "StringsSG/" followed by numbers is anywhere in the string
        if (!/StringsSG\/[0-9\.]+/.test(ua)) {
          setIsShowAndroidInstall(true);
        }
      }

      if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
        if (!/StringsSG\/[0-9\.]+/.test(ua)) {
          setIsShowIosInstall(true);
        }
      }
    } else {
      setIsHome(false)
    }
  }, [router.route, isUserLoggedIn])

  // Show loading screen while checking auth status ONLY on mobile app
  // For browsers (including search engine crawlers), render the non-logged-in 
  // content immediately for better SEO - page source will show actual content
  if (isCheckingLoginStatus && isMobileApp && !isUserLoggedIn) {
    return <HardLoadingCover />
  }

  return (
    <>
      {isUserLoggedIn ? (
        <>
          <HomeHeader />
          {(!router.route.includes("/home/chat") 
            && !router.route.includes("/home/sp-response") 
            && !router.route.includes("/team-job")
          ) &&
            <>
              {isShowAndroidInstall && (
                <div className="relative z-10 h-10">
                  <div className="bg-gray-200 fixed w-full">
                    <AndroidAppInstall />
                  </div>
                </div>
              )}
              {isShowIosInstall && (
                <div className="relative z-10 h-10">
                  <div className="bg-gray-200 fixed w-full">
                    <IosAppInstall />
                  </div>
                </div>
              )}
            </>
          }
          {isHome && <MenuBar />}
          <RequestProvider>
            <div className="pb-16 md:pb-0">
              <Component {...pageProps} />
            </div>
          </RequestProvider>
        </>
      ) : (
        <>
          <main className="pt-14">
            <Component {...pageProps} />
          </main>
          <IndexNav />
          {!router.route.includes("/home") &&
            !router.route.includes("/profile") &&
            !router.route.includes("/ai-chat") &&
            !router.route.includes("/shared-job") && <Footer />}
        </>
      )}
    </>
  )
}

function MyApp({ Component, pageProps }) {
  const [isLoading, setIsLoading] = useState(false)
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false)
  const [isShowFooter, setIsShowFooter] = useState(false)
  const [isGtagLoaded, setIsGtagLoaded] = useState(false)

  // Check if gtag is already defined (for cases where script loaded before React)
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      setIsGtagLoaded(true)
    }
  }, [])

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <PreserveScroll />
      
      {/* Google Tag Manager - loaded once globally with lazyOnload strategy */}
      <Script 
        id="google-tagmanager-js"
        src="https://www.googletagmanager.com/gtag/js?id=AW-823304067"
        strategy="lazyOnload"
        onLoad={() => {              
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'AW-823304067');
          window.gtag = gtag;
          setIsGtagLoaded(true)
        }} 
      />
      
      {/* Facebook Pixel - loaded once globally with lazyOnload strategy */}
      <Script id="facebook-tracking" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '128634041116395');
          fbq('track', 'PageView');`}
      </Script>
      
      <GtagContext.Provider value={{ isGtagLoaded, setIsGtagLoaded }}>
        <LocationProvider>
          <UserProvider
            setIsUserLoggedIn={setIsUserLoggedIn}
            isUserLoggedIn={isUserLoggedIn}
            setIsShowFooter={setIsShowFooter}
          >
            <AppContent 
              Component={Component} 
              pageProps={pageProps}
              setIsUserLoggedIn={setIsUserLoggedIn}
              isUserLoggedIn={isUserLoggedIn}
              setIsShowFooter={setIsShowFooter}
            />
          </UserProvider>
        </LocationProvider>
      </GtagContext.Provider>
      {isLoading && <HardLoadingCover />}
    </ErrorBoundary>
  )
}

export default MyApp
